import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from './Layout';
import StatusInfo from '../components/StatusInfo';
import { fmtMoney } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const LIMIT = 50;

// N-115: «Операции» — отдельный денежный контур, не привязан к календарному месяцу
// (свой статус "invoiced"/"paid" на каждый OperationInvoice, счёт создаётся
// автоматически при закрытии окна направления) — поэтому отдельная страница, а не
// вкладка на «Комиссии» (там всё завязано на фильтр месяца, который тут не нужен).

const INVOICE_BADGE = {
  invoiced: { style: 'badgeYellow', label: 'Ожидает оплаты' },
  paid: { style: 'badgeGreen', label: 'Оплачено' },
};

function InvoiceBadge({ status, styles }) {
  const b = INVOICE_BADGE[status] || INVOICE_BADGE.invoiced;
  return <span style={styles[b.style]}>{b.label}</span>;
}

export default function OperationCommissions() {
  const key = localStorage.getItem('adminKey');
  const headers = { 'X-Admin-Key': key };

  const [opItems, setOpItems] = useState([]);
  const [opTotal, setOpTotal] = useState(0);
  const [opSummary, setOpSummary] = useState({ total_amount: 0, total_owed: 0, invoiced_count: 0, paid_count: 0 });
  const [opOffset, setOpOffset] = useState(0);
  const [opSearch, setOpSearch] = useState('');
  const [opStatus, setOpStatus] = useState('');
  const [opSort, setOpSort] = useState('invoiced_at');
  const [opOrder, setOpOrder] = useState('desc');
  const [opBusyId, setOpBusyId] = useState(null);
  const [opMsg, setOpMsg] = useState('');
  const [showOpMarkPaid, setShowOpMarkPaid] = useState(null);
  const [opConfirmName, setOpConfirmName] = useState('');
  const [opPayoutModal, setOpPayoutModal] = useState(null);
  const [opPayoutFile, setOpPayoutFile] = useState(null);
  const [opPayoutRejecting, setOpPayoutRejecting] = useState(false);
  const [opPayoutRejectReason, setOpPayoutRejectReason] = useState('');
  const [opPayoutBusy, setOpPayoutBusy] = useState(false);
  const [opPayoutErr, setOpPayoutErr] = useState('');

  const fetchOperations = useCallback(() => {
    axios.get(`${API}/admin/operation-invoices`, {
      headers: { 'X-Admin-Key': key },
      params: { search: opSearch || undefined, status: opStatus || undefined, sort: opSort, order: opOrder, limit: LIMIT, offset: opOffset },
    }).then(r => { setOpItems(r.data.items); setOpTotal(r.data.total); setOpSummary(r.data.summary); });
    // eslint-disable-next-line
  }, [opSearch, opStatus, opSort, opOrder, opOffset, key]);

  useEffect(() => {
    const t = setTimeout(() => fetchOperations(), 250);
    return () => clearTimeout(t);
  }, [fetchOperations]);

  const opToggleSort = (f) => { setOpOffset(0); if (opSort === f) setOpOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setOpSort(f); setOpOrder('asc'); } };
  const opArrow = (f) => (opSort !== f ? '' : opOrder === 'asc' ? ' ▲' : ' ▼');

  const downloadOperationInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const r = await axios.get(`${API}/admin/operation-invoices/${invoiceId}/download`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${invoiceNumber || 'invoice'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { setOpMsg('Не удалось скачать счёт'); }
  };

  const doMarkOperationInvoicePaid = async () => {
    if (!showOpMarkPaid) return;
    setOpBusyId(`invoice-${showOpMarkPaid.invoice_id}`);
    setOpMsg('');
    try {
      await axios.patch(`${API}/admin/operation-invoices/${showOpMarkPaid.invoice_id}/mark-paid`, {}, { headers });
      setShowOpMarkPaid(null); setOpConfirmName('');
      fetchOperations();
    } catch (e) {
      setOpMsg(e?.response?.data?.detail || 'Не удалось отметить оплату');
    } finally { setOpBusyId(null); }
  };

  const payoutOperation = async (row) => {
    setOpBusyId(`payout-${row.referral_id}`);
    setOpMsg('');
    try {
      await axios.post(`${API}/admin/operations/${row.referral_id}/payout`, {}, { headers });
      fetchOperations();
    } catch (e) {
      setOpMsg(e?.response?.data?.detail || 'Не удалось выплатить');
    } finally { setOpBusyId(null); }
  };

  const openOpPayoutModal = (row) => {
    setOpPayoutModal({ referralId: row.referral_id, doctorName: row.doctor_name, ...row.payout_document });
    setOpPayoutFile(null); setOpPayoutRejecting(false); setOpPayoutRejectReason(''); setOpPayoutErr('');
  };
  const closeOpPayoutModal = () => setOpPayoutModal(null);

  const downloadOpPayoutDoc = async () => {
    try {
      const r = await axios.get(`${API}/admin/operations/${opPayoutModal.referralId}/payout-document/file`, { headers, responseType: 'blob' });
      window.open(window.URL.createObjectURL(r.data), '_blank');
    } catch { setOpPayoutErr('Не удалось открыть файл'); }
  };
  const acceptOpReceipt = async () => {
    setOpPayoutBusy(true); setOpPayoutErr('');
    try {
      await axios.post(`${API}/admin/operations/${opPayoutModal.referralId}/payout-document/accept`, {}, { headers });
      closeOpPayoutModal(); fetchOperations();
    } catch (e) { setOpPayoutErr(e?.response?.data?.detail || 'Не удалось принять чек'); }
    finally { setOpPayoutBusy(false); }
  };
  const rejectOpReceipt = async () => {
    if (!opPayoutRejectReason.trim()) { setOpPayoutErr('Укажите причину отказа'); return; }
    setOpPayoutBusy(true); setOpPayoutErr('');
    try {
      await axios.post(`${API}/admin/operations/${opPayoutModal.referralId}/payout-document/reject`, {}, { headers, params: { reason: opPayoutRejectReason } });
      closeOpPayoutModal(); fetchOperations();
    } catch (e) { setOpPayoutErr(e?.response?.data?.detail || 'Не удалось отклонить чек'); }
    finally { setOpPayoutBusy(false); }
  };
  const uploadOpAct = async () => {
    if (!opPayoutFile) { setOpPayoutErr('Выберите файл'); return; }
    setOpPayoutBusy(true); setOpPayoutErr('');
    try {
      const fd = new FormData();
      fd.append('file', opPayoutFile);
      await axios.post(`${API}/admin/operations/${opPayoutModal.referralId}/payout-document/act`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      closeOpPayoutModal(); fetchOperations();
    } catch (e) { setOpPayoutErr(e?.response?.data?.detail || 'Не удалось загрузить акт'); }
    finally { setOpPayoutBusy(false); }
  };
  const opPayoutButtonState = (pd) => {
    if (pd.kind === 'receipt') {
      if (pd.status === 'accepted') return { label: 'Чек принят', disabled: false };
      if (pd.status === 'pending_review') return { label: 'Открыть чек', disabled: false };
      return { label: 'Открыть чек', disabled: true };
    }
    if (pd.status === 'accepted') return { label: 'Акт принят', disabled: false };
    if (pd.status === 'sent') return { label: 'Акт отправлен', disabled: false };
    return { label: 'Загрузить акт', disabled: false };
  };

  return (
    <Layout>
      <div style={styles.header}>
        <h1 style={styles.title}>Комиссии операций</h1>
      </div>

      <div style={styles.headerControls}>
        <input
          style={styles.searchInput}
          placeholder="Поиск по врачу/клинике/ID врача/REF-номеру"
          value={opSearch}
          onChange={e => { setOpSearch(e.target.value); setOpOffset(0); }}
        />
        <select style={styles.monthSelect} value={opStatus} onChange={e => { setOpStatus(e.target.value); setOpOffset(0); }}>
          <option value="">Все статусы</option>
          <option value="invoiced">Ожидает оплаты</option>
          <option value="paid">Оплачено</option>
        </select>
        <div style={styles.opSummaryChip}>
          Всего: <b>{fmtMoney(opSummary.total_amount)} ₽</b> · должны нам: <b>{fmtMoney(opSummary.total_owed)} ₽</b>
        </div>
      </div>

      {opMsg && <div style={styles.errBanner}>{opMsg}</div>}
      {opItems.length === 0 ? (
        <div style={styles.empty}>Закрытых операционных направлений не найдено</div>
      ) : (
        <div style={styles.table}>
          <div style={{ ...styles.thead, gridTemplateColumns: opCols }}>
            <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => opToggleSort('doctor_name')}>Врач{opArrow('doctor_name')}</div>
            <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => opToggleSort('target_name')}>Клиника/сеть{opArrow('target_name')}</div>
            <div style={styles.th}>Номер направления</div>
            <div style={styles.th}>Период</div>
            <div style={styles.th}>Сумма оплат</div>
            <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => opToggleSort('total_amount')}>Врачу{opArrow('total_amount')}</div>
            <div style={styles.th}>Refero</div>
            <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => opToggleSort('status')}>Счёт{opArrow('status')}<StatusInfo def="operation_invoice" /></div>
            <div style={styles.th}>Действие</div>
          </div>
          {opItems.map(row => {
            const busy = opBusyId === `invoice-${row.invoice_id}` || opBusyId === `payout-${row.referral_id}`;
            return (
              <div key={row.referral_id} style={{ ...styles.row, gridTemplateColumns: opCols }}>
                <div style={styles.td}>
                  <a href={`/doctors/${row.doctor_id}`} style={styles.doctorLink}>{row.doctor_name}</a>
                  <div style={styles.sub}>{row.doctor_public_id || '—'}</div>
                  {!row.has_requisites && <div style={{ ...styles.sub, color: '#fca5a5' }}>нет реквизитов</div>}
                </div>
                <div style={styles.td}>
                  <a href={row.target_type === 'network' ? `/networks/${row.target_id}` : `/clinics/${row.target_id}`} style={styles.doctorLink}>{row.target_name}</a>
                </div>
                <div style={{ ...styles.td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#64748b', fontSize: 12 }}>REF-{row.referral_id}</div>
                <div style={{ ...styles.td, fontSize: 12, color: '#94a3b8' }}>
                  {row.window_start} – {row.window_end}
                </div>
                <div style={styles.td}>{fmtMoney(row.total_amount)} ₽ <span style={styles.muted}>({row.commission_pct}%)</span></div>
                <div style={{ ...styles.td, color: '#2EC4B6', fontWeight: 700 }}>{fmtMoney(row.doctor_amount)} ₽</div>
                <div style={{ ...styles.td, color: '#f59e0b' }}>{fmtMoney(row.platform_fee)} ₽</div>
                <div style={styles.td}><InvoiceBadge status={row.status} styles={styles} /></div>
                <div style={{ ...styles.td, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button style={styles.btnGhostSmall} disabled={busy} onClick={() => downloadOperationInvoice(row.invoice_id, row.invoice_number)}>Счёт</button>
                  {row.status === 'invoiced' && (
                    <button style={styles.btnPay} disabled={busy} onClick={() => { setShowOpMarkPaid(row); setOpConfirmName(''); }}>Оплачено</button>
                  )}
                  {row.status === 'paid' && !row.payout_document && (
                    <button
                      style={{ ...styles.btnPay, ...(row.has_requisites ? {} : styles.btnDisabledRed) }}
                      disabled={busy || !row.has_requisites}
                      title={row.has_requisites ? '' : 'У врача не заполнены реквизиты'}
                      onClick={() => payoutOperation(row)}
                    >Выплатить</button>
                  )}
                  {row.payout_document && (() => {
                    const btn = opPayoutButtonState(row.payout_document);
                    return (
                      <button
                        style={{ ...styles.btnGhostSmall, ...(btn.disabled ? styles.btnDisabled : {}) }}
                        disabled={btn.disabled}
                        title={row.payout_document.status === 'rejected' ? `Отклонён: ${row.payout_document.reject_reason}` : ''}
                        onClick={() => openOpPayoutModal(row)}
                      >{btn.label}</button>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showOpMarkPaid && (
        <div style={styles.modalOverlay} onClick={() => setShowOpMarkPaid(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Отметить счёт оплаченным?</h3>
            <p style={styles.modalText}>
              Подтвердите, что «{showOpMarkPaid.target_name}» перевела Refero {fmtMoney(showOpMarkPaid.total_to_pay)} ₽
              по счёту {showOpMarkPaid.invoice_number} (направление REF-{showOpMarkPaid.referral_id}).
              <br /><br />Для подтверждения введите точное название:
              <br /><b style={{ color: '#e2e8f0' }}>{showOpMarkPaid.target_name}</b>
            </p>
            <input style={styles.modalInput} placeholder="Название клиники/сети" value={opConfirmName}
              onChange={e => setOpConfirmName(e.target.value)} autoFocus />
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => setShowOpMarkPaid(null)}>Отмена</button>
              <button
                style={{ ...styles.btn, ...(opConfirmName.trim() === showOpMarkPaid.target_name.trim() ? {} : styles.btnDisabled) }}
                onClick={doMarkOperationInvoicePaid}
                disabled={opConfirmName.trim() !== showOpMarkPaid.target_name.trim()}
              >
                Оплачено
              </button>
            </div>
          </div>
        </div>
      )}

      {opPayoutModal && (
        <div style={styles.modalOverlay} onClick={closeOpPayoutModal}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              {opPayoutModal.kind === 'receipt' ? 'Чек' : 'Акт'} — {opPayoutModal.doctorName}
            </h3>
            {opPayoutErr && <div style={styles.errText}>{opPayoutErr}</div>}

            {opPayoutModal.kind === 'receipt' && (
              <>
                {opPayoutModal.status === 'not_uploaded' && (
                  <p style={styles.modalText}>Врач ещё не загрузил чек по этой операции.</p>
                )}
                {opPayoutModal.status === 'rejected' && (
                  <p style={styles.modalText}>Чек был отклонён: «{opPayoutModal.reject_reason}». Ожидаем повторной загрузки от врача.</p>
                )}
                {(opPayoutModal.status === 'pending_review' || opPayoutModal.status === 'accepted') && (
                  <>
                    <button style={styles.btnGhost} onClick={downloadOpPayoutDoc}>Скачать чек</button>
                    {opPayoutModal.status === 'accepted' && (
                      <p style={{ ...styles.modalText, marginTop: 12 }}><span style={styles.badgeGreen}>Принято</span></p>
                    )}
                    {opPayoutModal.status === 'pending_review' && !opPayoutRejecting && (
                      <div style={styles.modalActions}>
                        <button style={styles.btnGhost} onClick={() => setOpPayoutRejecting(true)}>Отклонить</button>
                        <button style={styles.btn} disabled={opPayoutBusy} onClick={acceptOpReceipt}>Принять</button>
                      </div>
                    )}
                    {opPayoutRejecting && (
                      <>
                        <textarea
                          style={styles.modalTextarea} rows={3} placeholder="Причина отказа"
                          value={opPayoutRejectReason} onChange={e => setOpPayoutRejectReason(e.target.value)}
                        />
                        <div style={styles.modalActions}>
                          <button style={styles.btnGhost} onClick={() => setOpPayoutRejecting(false)}>Назад</button>
                          <button style={styles.btn} disabled={opPayoutBusy} onClick={rejectOpReceipt}>Отклонить чек</button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}

            {opPayoutModal.kind === 'act' && (
              <>
                {(opPayoutModal.status === 'sent' || opPayoutModal.status === 'accepted') && (
                  <button style={styles.btnGhost} onClick={downloadOpPayoutDoc}>Скачать текущий акт</button>
                )}
                {opPayoutModal.status === 'accepted' ? (
                  <p style={{ ...styles.modalText, marginTop: 12 }}><span style={styles.badgeGreen}>Принято врачом</span></p>
                ) : (
                  <>
                    <input
                      type="file" accept=".pdf,.jpg,.jpeg,.png"
                      onChange={e => setOpPayoutFile(e.target.files[0])}
                      style={{ marginTop: 12, color: '#e2e8f0', fontSize: 13 }}
                    />
                    <div style={styles.modalActions}>
                      <button style={styles.btnGhost} onClick={closeOpPayoutModal}>Отмена</button>
                      <button style={styles.btn} disabled={opPayoutBusy} onClick={uploadOpAct}>
                        {opPayoutModal.status === 'sent' ? 'Перезалить акт' : 'Загрузить акт'}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {((opPayoutModal.kind === 'receipt' && (opPayoutModal.status === 'not_uploaded' || opPayoutModal.status === 'rejected'))
              || (opPayoutModal.kind === 'act' && opPayoutModal.status === 'accepted')) && (
              <div style={styles.modalActions}>
                <button style={styles.btnGhost} onClick={closeOpPayoutModal}>Закрыть</button>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}

const opCols = '1.6fr 1.6fr 1.1fr 1.3fr 1.2fr 1fr 1fr 1.1fr 1.8fr';

const styles = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, gap: 16 },
  headerControls: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  title: { color: 'white', fontSize: 24, fontWeight: 700, margin: 0 },
  monthSelect: {
    background: '#1e293b', color: 'white', border: '1px solid #334155',
    borderRadius: 8, padding: '8px 14px', fontSize: 14, cursor: 'pointer',
    outline: 'none',
  },
  empty: { color: '#64748b', fontSize: 15, textAlign: 'center', padding: '60px 0' },
  errBanner: { background: '#450a0a', color: '#fca5a5', border: '1px solid #7f1d1d', borderRadius: 10, padding: '10px 16px', fontSize: 13, marginBottom: 16 },
  table: { background: '#1e293b', borderRadius: 12, overflow: 'auto' },
  thead: { display: 'grid', padding: '12px 20px', borderBottom: '1px solid #334155', minWidth: 1000 },
  th: { fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  thClickable: { cursor: 'pointer', userSelect: 'none' },
  searchInput: { background: '#1e293b', color: 'white', border: '1px solid #334155', borderRadius: 8, padding: '8px 14px', fontSize: 14, outline: 'none' },
  opSummaryChip: { background: '#0f172a', color: '#94a3b8', border: '1px solid #334155', borderRadius: 8, padding: '8px 14px', fontSize: 13 },
  row: { display: 'grid', padding: '14px 20px', borderBottom: '1px solid #0f172a', alignItems: 'center', minWidth: 1000, transition: 'background 0.1s' },
  td: { fontSize: 13, color: '#e2e8f0' },
  sub: { fontSize: 11, color: '#64748b', marginTop: 2 },
  doctorLink: { color: '#e2e8f0', textDecoration: 'none', fontWeight: 600, fontSize: 13 },
  badgeGreen: { background: '#052e16', color: '#4ade80', padding: '3px 8px', borderRadius: 20, fontSize: 11, display: 'inline-block' },
  badgeYellow: { background: '#422006', color: '#fbbf24', padding: '3px 8px', borderRadius: 20, fontSize: 11, display: 'inline-block' },
  btnPay: { background: '#2EC4B6', color: 'white', border: 'none', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  btnGhostSmall: { background: 'transparent', color: '#94a3b8', border: '1px solid #334155', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnDisabled: { opacity: 0.4, cursor: 'default' },
  btnDisabledRed: { background: '#450a0a', color: '#fca5a5', border: '1px solid #7f1d1d', cursor: 'default' },
  muted: { color: '#475569', fontSize: 12 },
  btn: { background: '#2EC4B6', color: '#0f172a', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  modal: { background: '#1e293b', borderRadius: 12, padding: 24, width: 440, maxWidth: '90vw', border: '1px solid #334155' },
  modalTitle: { color: 'white', fontSize: 18, fontWeight: 700, margin: '0 0 12px' },
  modalText: { color: '#94a3b8', fontSize: 13, lineHeight: 1.5, margin: '0 0 16px' },
  modalInput: { width: '100%', boxSizing: 'border-box', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '9px 12px', fontSize: 14, outline: 'none', marginBottom: 16 },
  errText: { color: '#fca5a5', fontSize: 13, marginBottom: 12 },
  modalTextarea: { width: '100%', boxSizing: 'border-box', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '9px 12px', fontSize: 14, outline: 'none', marginTop: 12, fontFamily: 'inherit', resize: 'vertical' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 },
};
